import React, { ChangeEvent } from "react";

import CustomDropDown from "input/CustomDropDown";
import ManagedInput from "input/ManagedInput";
import ReviewSectionGenerator from "reports/ReviewSectionGenerator";
import AssessmentDeterminationPill from "reports/AssessmentDeterminationPill";
import { NotificationManagerAPI } from "notifications/NotificationManager";
import { discoveriesContainName, validateDiscoveries } from "reports/ReportDiscoveriesManager";
import { goalsContainName, validateGoals } from "reports/ReportGoalsManager";

import * as M from "reports/measures/Measure";
import * as MD from "reports/measures/MeasureDef";
import * as RD from "reports/ReportDef";
import * as SR from "reports/measures/SeverityResolver";
import * as T from "reports/measures/Trajectory";

// ---=== Constants ===--- //

export const customReportColors = ["#17B26A", "#F79009", "#488CE6", "#67E3F9", "#EE46BC"];

// ---=== Utilities ===--- //

export const isMonth6Report = (reportWeek: RD.ReportWeek): boolean => {
    return reportWeek === MD.Month6Token;
};

export const includeMeasureWeekInReport = (reportWeek: RD.ReportWeek, measureWeek: MD.MeasureWeek): boolean => {
    if (isMonth6Report(reportWeek)) {
        return M.isMonth6MeasureWeek(measureWeek) || (Number(measureWeek) > 0);
    }
    else {
        return (Number(measureWeek) > 0 && Number(reportWeek) >= Number(measureWeek));
    }
};

// export const includeMeasureWeekInReport = (reportWeek: RD.ReportWeek, measureWeek: MD.MeasureWeek): boolean => {
//     return (
//         (isMonth6Report(reportWeek) && M.isMonth6MeasureWeek(measureWeek))
//         || (!(isMonth6Report(reportWeek) && M.isMonth6MeasureWeek(measureWeek)) && (Number(measureWeek) > 0 && Number(reportWeek) >= Number(measureWeek)))
//     );
// };

const stringContainsName = (str: string, name: string): boolean => {
    return name.split(" ").some(part => str.toLowerCase().includes(part.toLowerCase()));
};

// ---=== Report Validation ===--- //

interface ValidateReportParams {
    assessmentsGenerated: boolean,
    behavioralAssessments: string,
    continuedNeedsAssessments: string,
    discoveries: RD.Discoveries,
    goals: RD.Goals,
    hasCoachingPlan: boolean,
    includeBehavioralAssessments: boolean,
    includeContinuedNeedsAssessments: boolean,
    includePhysicalAssessments: boolean,
    includeSocialAssessments: boolean,
    notificationManager: NotificationManagerAPI,
    physicalAssessments: string,
    plan: string,
    reportWeek: RD.ReportWeek,
    socialAssessments: string,
    subjectiveProgressSummary: string;
}

export const validateReport = ({
    assessmentsGenerated,
    behavioralAssessments,
    continuedNeedsAssessments,
    discoveries,
    goals,
    hasCoachingPlan,
    includeBehavioralAssessments,
    includeContinuedNeedsAssessments,
    includePhysicalAssessments,
    includeSocialAssessments,
    notificationManager,
    physicalAssessments,
    plan,
    reportWeek,
    socialAssessments,
    subjectiveProgressSummary
}: ValidateReportParams): boolean => {
    const generalReportErrors: (string | Error)[] = [];
    const subjectiveOutcomesErrors: (string | Error)[] = [];
    const assessmentsErrors: (string | Error)[] = [];
    const planErrors: (string | Error)[] = [];
    let isValid: boolean = true;

    // ---=== General Report Errors ===--- //

    if (reportWeek === 0) {
        generalReportErrors.push("Cannot create reports during Coaching Plan Week 0.");
    }

    if (!hasCoachingPlan) {
        generalReportErrors.push("Cannot create reports without an active Coaching Plan.");
    }

    if (generalReportErrors.length > 0) {
        notificationManager.addError(generalReportErrors);
        isValid = false;
    }
    else {

        // ---=== Subjective Outcomes Errors ===--- //

        if (subjectiveProgressSummary.trim() === "" ) {
            subjectiveOutcomesErrors.push("Subjective Outcomes: Provide a Subjective Progress Summary.");
        }

        let [_validatedGoals, goalErrors] = validateGoals(goals);
        goalErrors && subjectiveOutcomesErrors.push(...goalErrors);

        let [_validatedDiscoveries, discoveryErrors] = validateDiscoveries(discoveries);
        discoveryErrors && subjectiveOutcomesErrors.push(...discoveryErrors);

        if (subjectiveOutcomesErrors.length > 0) {
            notificationManager.addError(subjectiveOutcomesErrors);
            isValid = false;
        }

        // ---=== Assessments Errors ===--- //

        if (!assessmentsGenerated) {
            assessmentsErrors.push("Assessments: Assessments must be generated.")
        }
        else {
            if (!includeBehavioralAssessments && !includePhysicalAssessments && !includeSocialAssessments && !includeContinuedNeedsAssessments) {
                assessmentsErrors.push("Assessments: Select at least one assessment section to include.");
            }
            if (includeBehavioralAssessments && behavioralAssessments.length > 2000) {
                assessmentsErrors.push("Assessments: Behavioral Assessments are above character limit.");
            }
            if (includePhysicalAssessments && physicalAssessments.length > 2000) {
                assessmentsErrors.push("Assessments: Physical Assessments are above character limit.");
            }
            if (includeSocialAssessments && socialAssessments.length > 2000) {
                assessmentsErrors.push("Assessments: Social Assessments are above character limit.");
            }
            if (includeContinuedNeedsAssessments && continuedNeedsAssessments.length > 2500) {
                assessmentsErrors.push("Assessments: Continued Needs Assessments are above character limit.");
            }
        }

        if (assessmentsErrors.length > 0) {
            notificationManager.addError(assessmentsErrors);
            isValid = false;
        }

        // ---=== Plan Errors ===--- //

        if (plan.trim() === "" ) {
            planErrors.push("Plan: Provide a Plan template.");
        }

        if (planErrors.length > 0) {
            notificationManager.addError(planErrors);
            isValid = false;
        }
    }

    return isValid;
};

interface ValidatePatientNameParams {
    behavioralAssessments: string,
    discoveries: RD.Discoveries,
    goals: RD.Goals,
    patientName: string;
    payerType: RD.PayerEnums;
    physicalAssessments: string;
    plan: string;
    socialAssessments: string;
    subjectiveProgressSummary: string;
}

export const validatePatientName = ({
    behavioralAssessments,
    discoveries,
    goals,
    patientName,
    payerType,
    physicalAssessments,
    plan,
    socialAssessments,
    subjectiveProgressSummary
}: ValidatePatientNameParams): boolean => {
    if (payerType === RD.PayerEnums.VA) {
        let nameWarnings = [];

        if (stringContainsName(plan, patientName)) {
            nameWarnings.push("Final Plan");
        }
        if (stringContainsName(behavioralAssessments, patientName)) {
            nameWarnings.push("Assessments: Behavioral");
        }
        if (stringContainsName(physicalAssessments, patientName)) {
            nameWarnings.push("Assessments: Physical");
        }
        if (stringContainsName(socialAssessments, patientName)) {
            nameWarnings.push("Assessments: Social");
        }
        if (stringContainsName(subjectiveProgressSummary, patientName)) {
            nameWarnings.push("Summary");
        }
        if (goalsContainName(goals, patientName)) {
            nameWarnings.push("Subjective Outcomes: Goals");
        }
        if (discoveriesContainName(discoveries, patientName)) {
            nameWarnings.push("Subjective Outcomes: Discoveries");
        }

        if (nameWarnings.length > 0) {
            let text = `WARNING\n\nVA patients should have their names hidden in reports!\n\nPart or all of the patient name '${patientName}' was found in:\n\n${nameWarnings.join("\n")}\n\nAre you sure you wish to submit the report?`;
            if (!window.confirm(text)) {
                return false;
            }
        }
    }

    return true;
};

// ---=== Render ===--- //

export const renderReportHeader = ({
    hasPhysicianOversight,
    isGenerator,
    reportWeek,
    reportWeekOptions,
    // Callbacks
    reportWeekChangeCallback
}: RD.RenderReportHeaderPayload) => {
    return (
        <React.Fragment key={"report-header"}>
            {
                isGenerator
                    ? (
                        <div id="report-header" className="w-full grid grid-cols-[1fr_auto_1fr] justify-center items-center mt-3 mb-4">
                            <div id="left-content-tooltip" className="flex justify-end items-center mr-6">
                                <div className="inline-flex items-center text-gray-700 text-sm font-medium leading-tight">
                                    <span className="text-gray-900">Report Week</span>
                                    <span className="pl-1 relative">
                                        <div className="has-tooltip flex items-center gap-1">
                                            <img className="h-5 w-5 relative hs-tooltip" src="/icons/help-circle.svg" alt="help icon" />
                                            <span className="tooltip rounded shadow-lg whitespace-normal break-normal min-w-[5vw] max-w-[40vw] w-max mb-2 ml-2">
                                                Choose which week to include in the report. Data will auto-populate from Practice Better notes and VET activities up to this week.
                                            </span>
                                        </div>
                                    </span>
                                </div>
                            </div>
                            <div id="center-content-week-selector" className="flex justify-center items-center text-gray-900 text-lg font-semibold leading-8">
                                <div className="justify-start items-center inline-flex gap-x-4">
                                    <div className="w-fit">
                                        <CustomDropDown
                                            defaultValue={reportWeek}
                                            options={reportWeekOptions}
                                            optionsAlignRight={false}
                                            // Callbacks
                                            onSelectionCallback={reportWeekChangeCallback}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div id="right-content-physician-oversight" className="flex justify-start items-center ml-6 gap-1 whitespace-nowrap">
                                <ReviewSectionGenerator hasPhysicianOversight={hasPhysicianOversight}/>
                            </div>
                        </div>
                    )
                    : (
                        <div className="w-full justify-center items-center inline-flex mt-3 mb-4">
                            <ReviewSectionGenerator hasPhysicianOversight={hasPhysicianOversight}/>
                        </div>
                    )
            }
        </React.Fragment>
    );
};

export const renderTrajectoryTooltip = (tooltip: string) => {
    if (!tooltip || tooltip === "") {
        return null;
    }

    return (
        <React.Fragment>
            <span className="relative inline-flex">
                <div className="has-tooltip relative pl-1 inline-flex">
                    <img className="w-3.5 h-3.5 relative hs-tooltip" src={"/icons/info-circle-measure.svg"} alt="icon"/>
                    <span className="tooltip rounded shadow-lg whitespace-normal break-normal min-w-[10vw] max-w-[40vw] [@media(max-width:1200px)]:max-w-[45vw] [@media(min-width:1600px)]:max-w-[30vw] w-max bottom-1">
                        {tooltip}
                    </span>
                </div>
            </span>
        </React.Fragment>
    );
};

export const renderNoTrajectoryValueDecorator = () => {
    return (
        <React.Fragment>
            <div className="pl-1.5 py-0.5 mx-1.5 bg-error-50 rounded-full border border-error-200 justify-start items-center gap-1.5 inline-flex">
                <img className="w-3 h-3 relative" src="/icons/warning.svg" alt="icon"/>
                <div className="pr-2.5 text-center text-error-700 text-xs font-medium leading-[18px]">
                    No Data
                </div>
            </div>
        </React.Fragment>
    );
};

export const renderBandTrajectoryWeekDecorator = (measureWeek: MD.MeasureWeek) => {
    return (
        <React.Fragment>
            <div className="pl-1.5 pr-2 py-0.5 mx-1.5 bg-brand-50 rounded-full border border-brand-200 justify-start items-center gap-1.5 inline-flex">
                <img className="w-3 h-3 relative" src="/icons/clock-rewind.svg" alt="icon"/>
                <div className="pr-3 text-center text-brand-700 text-xs font-medium leading-[18px]">
                    {`Week ${measureWeek}`}
                </div>
            </div>
        </React.Fragment>
    );
};

export const renderBandTrajectoryDeltaDecorator = (mt: T.Trajectory) => {
    let firstWeek: MD.MeasureWeek = T.firstMeasure(mt).week;

    if (mt.comparedDelta === 0) {
        return (
            <React.Fragment>
                <div className=" pl-1.5 pr-2 py-0.5 mx-1.5 bg-gray-50 rounded-full border border-gray-200 justify-start items-center gap-1.5 inline-flex">
                    <img className="w-3 h-3 relative" src="/icons/minus.svg" alt="icon"/>
                    <div className="text-center text-gray-700 text-xs font-medium leading-[18px]">
                        {`No Change from Week ${firstWeek}`}
                    </div>
                </div>
            </React.Fragment>
        );
    }

    const getDecoratorStyles = (change: number, lowerIsBetter: boolean): string[] => {
        const styles = {
            success: ["bg-success-50", "border-success-200", "text-success-700"],
            warning: ["bg-warning-50", "border-warning-200", "text-warning-700"]
        };

        if (!lowerIsBetter) {
            return [
                ...(change > 0 ? styles.success : styles.warning),
                change > 0 ? "upgood.svg" : "downBad.svg"
            ];
        }
        return [
            ...(change > 0 ? styles.warning : styles.success),
            change > 0 ? "upbad.svg" : "downgood.svg"
        ];
    };

    const [bgColor, borderColor, textColor, iconSrc] = getDecoratorStyles(mt.comparedDelta, M.lowerIsBetter(mt.measureType));
    const precisionMeasureTypes: MD.MeasureType[] = [MD.MeasureTypesMap.WHODAS];
    const displayOptions: T.DisplayComparedDeltaOptions = {
        precision: precisionMeasureTypes.includes(mt.measureType) ? 2 : 0,
        round: true,
        absolute: true,
        percent: M.isRomType(mt.measureType)
    };

    return (
        <React.Fragment>
            <div className={`pl-1.5 pr-2 py-0.5 mx-1.5 ${bgColor} rounded-full border ${borderColor} justify-start items-center gap-1.5 inline-flex`}>
                <img className="w-3 h-3 relative" src={`/icons/${iconSrc}`} alt="icon"/>
                <div className={`text-center ${textColor} text-xs font-medium leading-[18px]`}>
                    {`${T.displayComparedDelta(mt, displayOptions)} from Week ${firstWeek}`}
                </div>
            </div>
        </React.Fragment>
    );
};

export const renderBandSeverityTrajectoryDecorator = (mt: T.Trajectory) => {
    let resolver: SR.SeverityDetails = M.severityResolver(mt.comparedMeasure);
    let svg: string;

    if (resolver.severity >= SR.Severity.SEVERE) {
        svg = "info-circle-error";
    }
    else if (resolver.severity >= SR.Severity.MILD) {
        svg = "info-circle-warning";
    }
    else {
        svg = "info-circle-success";
    }

    return (
        <React.Fragment>
            <span className="relative">
                <div className={`has-tooltip pl-0.5 pr-2 py-0.5 mx-1.5 rounded-full border justify-start items-center gap-1.5 inline-flex ${resolver.colors.bg} ${resolver.colors.border}`}>
                    <span className="tooltip rounded shadow-lg whitespace-normal break-normal min-w-[10vw] max-w-[30vw] [@media(max-width:1200px)]:max-w-[40vw] [@media(min-width:1600px)]:max-w-[30vw] w-max bottom-[125%] right-1">
                        {resolver.description}
                    </span>
                    <div className="relative pl-1 inline-flex">
                        <img className="w-3 h-3 relative hs-tooltip" src={`/icons/${svg}.svg`} alt="icon"/>
                    </div>
                    <div className={`text-center text-xs font-medium leading-[18px] ${resolver.colors.text}`}>
                        {resolver.label}
                    </div>
                </div>
            </span>
        </React.Fragment>
    );
};

export const renderKeyImprovedTrajectory = ({
    disabled,
    included,
    reportWeek,
    trajectory,
    assessmentDecisions,
    // Callbacks
    toggleTrajectoryCallback
}: RD.RenderTrajectoryPayload) => {

    let measureType: MD.MeasureType = trajectory.measureType;
    let measures: MD.Measures = trajectory.measures;
    let hasMeasures: boolean = measures?.length > 0;
    let comparedMeasure: MD.Measure = trajectory.comparedMeasure;
    let tooltip: string = M.tooltip(measureType);

    return (
        <React.Fragment key={`trajectory-${trajectory.measureType.toLowerCase().trim().replace(/\s+/g, "-")}`}>
            <tr className="border-0">
                <td className="inline-flex items-center px-4 py-2 whitespace-nowrap">
                    <label className={`flex gap-x-2 justify-start items-center text-base font-normal leading-normal ${disabled ? "text-gray-400" : "text-gray-600"}`}>
                        <input
                            className="w-4 h-4 p-0.5 bg-gray-50 rounded border border-gray-300 justify-start items-start flex cursor-pointer disabled:cursor-not-allowed"
                            type="checkbox"
                            checked={included}
                            onChange={
                                (event: ChangeEvent<HTMLInputElement>) => {
                                    toggleTrajectoryCallback(measureType, event.target.checked);
                                }
                            }
                            disabled={disabled || !hasMeasures}
                        />
                        {trajectory.measureName}
                    </label>
                    {
                        tooltip &&
                        renderTrajectoryTooltip(tooltip)
                    }
                </td>
                <td className="px-12 py-2 text-center font-normal text-gray-600 leading-normal">
                    {
                        hasMeasures
                            ? M.displayAsUnits(comparedMeasure)
                            : renderNoTrajectoryValueDecorator()
                    }
                </td>
                <td className="px-0 py-2 text-base font-normal text-gray-600 leading-normal">
                    {
                        hasMeasures && comparedMeasure.week !== reportWeek &&
                        renderBandTrajectoryWeekDecorator(comparedMeasure.week)
                    }
                </td>
                <td className="px-0 py-2 text-base font-normal text-gray-600 leading-normal">
                    {
                        hasMeasures && measures.length > 1 &&
                        renderBandTrajectoryDeltaDecorator(trajectory)
                    }
                </td>
                <td className="px-0 py-2 text-base font-normal text-gray-600 leading-normal">
                    {
                        hasMeasures &&
                        renderBandSeverityTrajectoryDecorator(trajectory)
                    }
                </td>
                <td className="px-0 py-2 text-center text-base font-normal text-gray-600 leading-normal">
                    {
                        hasMeasures && assessmentDecisions &&
                            <AssessmentDeterminationPill measureType={measureType} decisions={assessmentDecisions} />
                    }
                </td>
            </tr>
        </React.Fragment>
    );
};

export const trajectoryCellBaseClasses = "py-2 text-base font-normal leading-normal flex items-center bg-gray-25";
export const trajectoryIncluderCellClasses = `${trajectoryCellBaseClasses} pl-7 whitespace-nowrap justify-start`;
export const trajectoryValueCellClasses = `${trajectoryCellBaseClasses} px-12 text-center text-gray-600 justify-center`;
export const trajectoryWeekCellClasses = `${trajectoryCellBaseClasses} text-left text-gray-600 justify-start`;
export const trajectoryDeltaCellClasses = `${trajectoryCellBaseClasses} text-left text-gray-600 justify-start`;
export const trajectorySeverityCellClasses = `${trajectoryCellBaseClasses} pr-3 text-left text-gray-600 justify-start`;

export const renderTrajectory = ({
    disabled,
    included,
    reportWeek,
    trajectory,
    assessmentDecisions,
    // Callbacks
    toggleTrajectoryCallback
}: RD.RenderTrajectoryPayload) => {
    let measureType: MD.MeasureType = trajectory.measureType;
    let measures: MD.Measures = trajectory.measures;
    let hasMeasures: boolean = measures?.length > 0;
    let comparedMeasure: MD.Measure = trajectory.comparedMeasure;
    let tooltip: string = M.tooltip(measureType);
    let id = `trajectory-${measureType.toString().toLowerCase().trim().replace(/\s+/g, "-")}`;

    const getCellSideBorderClasses = (colIndex: number) => {
        let classes = "border-gray-300 ";
        if (colIndex === 1) classes += "border-l ";
        if (colIndex === 6) classes += "border-r ";
        return classes;
    };

    return(
        <div key={id} className="contents">
            <div className={`${trajectoryIncluderCellClasses} ${getCellSideBorderClasses(1)} col-start-1`}>
                <label className={`flex gap-x-2 justify-start items-center ${disabled ? "text-gray-400" : "text-gray-600"}`}>
                    <ManagedInput
                        className="w-4 h-4 p-0.5 bg-gray-50 rounded border border-gray-300 cursor-pointer disabled:cursor-not-allowed"
                        id={id}
                        type="checkbox"
                        value={included}
                        updateValue={checked => toggleTrajectoryCallback(measureType, checked)}
                        disabled={disabled || !hasMeasures}
                    />
                    {trajectory.measureName}
                </label>
                {
                    tooltip &&
                    renderTrajectoryTooltip(tooltip)
                }
            </div>
            <div className={`${trajectoryValueCellClasses} ${getCellSideBorderClasses(2)} col-start-2`}>
                {
                    hasMeasures
                        ? M.displayAsUnits(comparedMeasure)
                        : renderNoTrajectoryValueDecorator()
                }
            </div>
            <div className={`${trajectoryWeekCellClasses} ${getCellSideBorderClasses(3)} col-start-3`}>
                {
                    hasMeasures && comparedMeasure.week !== reportWeek &&
                        renderBandTrajectoryWeekDecorator(comparedMeasure.week)
                }
            </div>
            <div className={`${trajectoryDeltaCellClasses} ${getCellSideBorderClasses(4)} col-start-4`}>
                {
                    hasMeasures && measures.length > 1 &&
                        renderBandTrajectoryDeltaDecorator(trajectory)
                }
            </div>
            <div className={`${trajectorySeverityCellClasses} ${getCellSideBorderClasses(5)} col-start-5`}>
                {
                    hasMeasures &&
                        renderBandSeverityTrajectoryDecorator(trajectory)
                }
            </div>
            <div className={`${trajectorySeverityCellClasses} ${getCellSideBorderClasses(6)} col-start-6 text-center`}>
                {
                    hasMeasures && assessmentDecisions &&
                        <AssessmentDeterminationPill measureType={measureType} decisions={assessmentDecisions} />
                }
            </div>
        </div>
    );
};

export const renderKeyImprovedTrajectoriesSection = ({
    isGenerator,
    assessmentsGenerated,
    renderPayload,
    reportWeek
}: RD.RenderKeyImprovedTrajectoriesSectionPayload) => {
    return (
        <React.Fragment key={`key-improved-trajectories`}>
            <div id="key-improved-trajectories" className="w-fit bg-gray-25 border-1 border-gray-300 m-2 p-3 rounded-md">
                <table>
                    <tbody>
                        {
                            isGenerator && (
                                <tr>
                                    <td colSpan={5}>
                                        <div className="flex items-center gap-3 mx-2 mt-2 mb-4 p-2 bg-yellow-50 border border-orange-500 rounded">
                                            <img className="w-[18px] h-[18px] relative bg-gray-50 rounded-full" src="/icons/infoOrange.svg" alt="icon"/>
                                            <div className="grow shrink basis-0 flex-col justify-start items-start inline-flex text-wrap">
                                                <div className="self-stretch text-wrap text-gray-600 text-base max-[1200px]:text-sm font-normal leading-normal">
                                                    {
                                                        assessmentsGenerated
                                                            ? "To edit included measures, they must be unlocked in the Objective Outcomes section."
                                                            : (
                                                                <span
                                                                    dangerouslySetInnerHTML={
                                                                        {
                                                                            __html: `Choose which Objective Outcome measures to include in the report <b>BEFORE</b> generating Assessments.<br/>`
                                                                                        + `Changing included measures after generation will cause any edited Assessments to be lost.`
                                                                        }
                                                                    }
                                                                />
                                                            )

                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            )
                        }
                        {
                            renderPayload?.map(
                                (payload: RD.RenderKeyImprovedTrajectoryPayload) => {
                                    // let trajectory = trajectoryRenderPayload.trajectory;
                                    // console.log(`Render - ${trajectory.type} ${T.Trajectory[trajectory.trajectory]} ${trajectory.normalizedPercent}%`);
                                    return renderKeyImprovedTrajectory({
                                        ...payload,
                                        reportWeek
                                    });
                                }
                            )
                        }
                    </tbody>
                </table>
            </div>
        </React.Fragment>
    );
};

export interface RenderObjectiveOutcomeTrajectoriesGridPayload {
    allTrajectories: T.Trajectories;
    assessmentsGenerated: boolean;
    includeAverageTime: boolean;
    includeMedicationReduction: boolean;
    includeReturnToWork: boolean;
    includeSessionsAttended: boolean;
    includeUniqueDays: boolean;
    includedTrajectories: Record<MD.MeasureType, boolean>,
    isGenerator: boolean;
    reportWeek: MD.MeasureWeek;
    sessionsAttended: number;
    uniqueDays: number;
    averageActivityTimePerWeek: string;
    returnToWorkStatus: { week: MD.MeasureWeek, value: number, label: string };
    medicationReductionStatus: { week: MD.MeasureWeek, value: number, label: string };
    assessmentDecisions?: any[]; // AssessmentDecision[] - using any to avoid circular dependency
    // Callbacks
    setIncludeAverageTime: React.Dispatch<React.SetStateAction<boolean>>
    setIncludeMedicationReduction: React.Dispatch<React.SetStateAction<boolean>>
    setIncludeReturnToWork: React.Dispatch<React.SetStateAction<boolean>>
    setIncludeSessionsAttended: React.Dispatch<React.SetStateAction<boolean>>
    setIncludeUniqueDays: React.Dispatch<React.SetStateAction<boolean>>
    toggleIncludedTrajectoryCallback: (measureType: MD.MeasureType, include: boolean) => void;
}

export const RenderObjectiveOutcomeTrajectoriesGrid = ({
    allTrajectories,
    includeAverageTime,
    includeMedicationReduction,
    includeReturnToWork,
    includeSessionsAttended,
    includeUniqueDays,
    includedTrajectories,
    isGenerator,
    assessmentsGenerated,
    reportWeek,
    sessionsAttended,
    uniqueDays,
    averageActivityTimePerWeek,
    returnToWorkStatus,
    medicationReductionStatus,
    assessmentDecisions,
    // Callbacks
    setIncludeAverageTime,
    setIncludeMedicationReduction,
    setIncludeReturnToWork,
    setIncludeSessionsAttended,
    setIncludeUniqueDays,
    toggleIncludedTrajectoryCallback
}: RenderObjectiveOutcomeTrajectoriesGridPayload) => {

    const mayIncludeMedicationReductionStatusInReport = () => {
        return medicationReductionStatus !== null && includeMeasureWeekInReport(reportWeek, medicationReductionStatus.week);
    };

    const mayIncludeReturnToWorkStatusInReport = () => {
        return returnToWorkStatus !== null && includeMeasureWeekInReport(reportWeek, returnToWorkStatus.week);
    };

    const renderTrajectoriesSection = ({ sectionTitle, measureTypes }: { sectionTitle: string, measureTypes: MD.MeasureTypes }) => {
        const trajectories = measureTypes.map(type =>
            allTrajectories.find(trajectory => trajectory.measureType === type)
        ).filter(Boolean);

        const elements = [];

        elements.push(
            <div key={`${sectionTitle}-header`} className="col-span-6 pl-7 py-1 text-gray-900 text-lg font-semibold leading-7 bg-gray-100 border-t border-l border-r border-gray-300 rounded-t-md shadow-sm mt-4 first:mt-0">
                {sectionTitle}
            </div>
        );
        elements.push(
            <div key={`${sectionTitle}-header-bottom-margin`} className="col-span-6 h-3 border-l border-r bg-gray-25 border-gray-300"></div>
        );

        trajectories.forEach(
            (trajectory: T.Trajectory) => {
                elements.push(
                    renderTrajectory({
                        trajectory: trajectory,
                        included: includedTrajectories[trajectory.measureType],
                        disabled: isGenerator && assessmentsGenerated,
                        reportWeek,
                        assessmentDecisions,
                        // Callbacks
                        toggleTrajectoryCallback: toggleIncludedTrajectoryCallback
                    })
                );
            });

        elements.push(
            <div key={`${sectionTitle}-bottom-border`} className="col-span-6 h-3 bg-gray-25 border-b border-l border-r border-gray-300 rounded-b-md"></div>
        );

        return elements;
    };

    const categories = [
        { title: "Physical Disability", measureTypes: [MD.MeasureTypesMap.DASH, MD.MeasureTypesMap.NDI, MD.MeasureTypesMap.Oswestry, MD.MeasureTypesMap.WHODAS] },
        { title: "Physical Function", measureTypes: [MD.MeasureTypesMap.Activity, MD.MeasureTypesMap.KarunaRating, MD.MeasureTypesMap.Pain, MD.MeasureTypesMap.PCSS, MD.MeasureTypesMap.Sleep, MD.MeasureTypesMap.UEFI] },
        { title: "Range of Motion", measureTypes: [MD.MeasureTypesMap.LeftAbduction, MD.MeasureTypesMap.LeftFlexion, MD.MeasureTypesMap.LeftScaption, MD.MeasureTypesMap.RightAbduction, MD.MeasureTypesMap.RightFlexion, MD.MeasureTypesMap.RightScaption, MD.MeasureTypesMap.LowerFlexion, MD.MeasureTypesMap.NeckFlexion, MD.MeasureTypesMap.NeckExtension, MD.MeasureTypesMap.NeckLeftRotation, MD.MeasureTypesMap.NeckRightRotation] },
        { title: "Behavioral", measureTypes: [MD.MeasureTypesMap.Confidence, MD.MeasureTypesMap.FABQActivity, MD.MeasureTypesMap.FABQTotal, MD.MeasureTypesMap.FABQWork, MD.MeasureTypesMap.GAD7, MD.MeasureTypesMap.Mood, MD.MeasureTypesMap.PCS, MD.MeasureTypesMap.PHQ9, MD.MeasureTypesMap.PSEQ] }
    ];

    return (
        <div id="objective-outcomes-trajectories-grid" className="m-2 grid grid-cols-[repeat(6,_max-content)] gap-x-0 gap-y-0">
            {/* --- Adherence Category --- */}
            <div className="col-span-6 pl-7 py-1 text-gray-900 text-lg font-semibold leading-7 bg-gray-100 border-t border-l border-r border-gray-300 rounded-t-md shadow-sm mt-4 first:mt-0">
                Adherence
            </div>
            <div className="col-span-6 h-3 bg-gray-25 border-l border-r border-gray-300"></div>
            <div className="contents bg-gray-25">
                <div className={`${trajectoryIncluderCellClasses} border-l border-gray-300 col-start-1`}>
                    <label className={`flex gap-x-2 justify-start items-center ${sessionsAttended ? "text-gray-600 " : "text-gray-400"}`}>
                        <input
                            className="w-4 h-4 p-0.5 bg-gray-50 rounded border border-gray-300 cursor-pointer disabled:cursor-not-allowed"
                            type="checkbox"
                            checked={includeSessionsAttended}
                            disabled={(isGenerator && assessmentsGenerated) || !sessionsAttended}
                            onChange={_e => setIncludeSessionsAttended(prev => !prev)}
                        />
                        Coaching Sessions Attended
                    </label>
                </div>
                <div className={`${trajectoryValueCellClasses} col-start-2`}>
                    {sessionsAttended != null ? sessionsAttended : renderNoTrajectoryValueDecorator()}
                </div>
                <div className={`${trajectoryWeekCellClasses} col-start-3`}></div>
                <div className={`${trajectoryDeltaCellClasses} col-start-4`}></div>
                <div className={`${trajectorySeverityCellClasses} col-start-5`}></div>
                <div className={`${trajectorySeverityCellClasses} border-r border-gray-300 col-start-6`}></div>
            </div>
            <div className="contents bg-gray-25">
                <div className={`${trajectoryIncluderCellClasses} border-l border-gray-300 col-start-1`}>
                    <label className={`flex gap-x-2 justify-start items-center ${uniqueDays ? "text-gray-600" : "text-gray-400"}`}>
                        <input
                            className="w-4 h-4 p-0.5 bg-gray-50 rounded border border-gray-300 cursor-pointer disabled:cursor-not-allowed"
                            type="checkbox"
                            checked={includeUniqueDays}
                            disabled={(isGenerator && assessmentsGenerated) || !uniqueDays}
                            onChange={_e => setIncludeUniqueDays(prev => !prev)}
                        />
                        Unique Days in Headset Per Week
                    </label>
                </div>
                <div className={`${trajectoryValueCellClasses} col-start-2`}>
                    {uniqueDays !== null ? uniqueDays : renderNoTrajectoryValueDecorator()}
                </div>
                <div className={`${trajectoryWeekCellClasses} col-start-3`}></div>
                <div className={`${trajectoryDeltaCellClasses} col-start-4`}></div>
                <div className={`${trajectorySeverityCellClasses} col-start-5`}></div>
                <div className={`${trajectorySeverityCellClasses} border-r border-gray-300 col-start-6`}></div>
            </div>
            <div className="contents bg-gray-25">
                <div className={`${trajectoryIncluderCellClasses} border-l border-gray-300 col-start-1`}>
                    <label className={`flex gap-x-2 justify-start items-center ${averageActivityTimePerWeek ? "text-gray-600 " : "text-gray-400"}`}>
                        <input
                            className="w-4 h-4 p-0.5 bg-gray-50 rounded border border-gray-300 cursor-pointer disabled:cursor-not-allowed"
                            type="checkbox"
                            checked={includeAverageTime}
                            disabled={(isGenerator && assessmentsGenerated) || !averageActivityTimePerWeek}
                            onChange={_e => setIncludeAverageTime(prev => !prev)}
                        />
                        Average Time in Activities Per Week
                    </label>
                </div>
                <div className={`${trajectoryValueCellClasses} col-start-2`}>
                    {averageActivityTimePerWeek ? averageActivityTimePerWeek : renderNoTrajectoryValueDecorator()}
                </div>
                <div className={`${trajectoryWeekCellClasses} col-start-3`}></div>
                <div className={`${trajectoryDeltaCellClasses} col-start-4`}></div>
                <div className={`${trajectorySeverityCellClasses} col-start-5`}></div>
                <div className={`${trajectorySeverityCellClasses} border-r border-gray-300 col-start-6`}></div>
            </div>
            <div className="col-span-6 h-3 bg-gray-25 border-b border-l border-r border-gray-300 rounded-b-md"></div>
            {/* --- End Adherence Category --- */}

            {/* --- Program Impact Category --- */}
            <div className="col-span-6 pl-7 py-1 text-gray-900 text-lg font-semibold leading-7 bg-gray-100 border-t border-l border-r border-gray-300 rounded-t-md shadow-sm mt-4 first:mt-0">
                Program Impact
            </div>
            <div className="col-span-6 h-3 bg-gray-25  border-l border-r border-gray-300"></div>
            <div className="contents bg-gray-25">
                <div className={`${trajectoryIncluderCellClasses} border-l border-gray-300 col-start-1`}>
                    <label className={`flex gap-x-2 justify-start items-center ${mayIncludeReturnToWorkStatusInReport() ? "text-gray-600" : "text-gray-400"}`}>
                        <input
                            className="w-4 h-4 p-0.5 bg-gray-50 rounded border border-gray-300 cursor-pointer disabled:cursor-not-allowed"
                            type="checkbox"
                            checked={includeReturnToWork}
                            disabled={(isGenerator && assessmentsGenerated) || !mayIncludeReturnToWorkStatusInReport()}
                            onChange={_e => setIncludeReturnToWork(prev => !prev)}
                        />
                        Return To Work
                    </label>
                </div>
                <div className={`${trajectoryValueCellClasses} col-start-2`}>
                    {
                        mayIncludeReturnToWorkStatusInReport()
                            ? returnToWorkStatus.label
                            : renderNoTrajectoryValueDecorator()
                    }
                </div>
                <div className={`${trajectoryWeekCellClasses} col-start-3`}>
                    {
                        mayIncludeReturnToWorkStatusInReport() && reportWeek !== returnToWorkStatus.week &&
                        renderBandTrajectoryWeekDecorator(returnToWorkStatus.week)
                    }
                </div>
                <div className={`${trajectoryDeltaCellClasses} col-start-4`}></div>
                <div className={`${trajectorySeverityCellClasses} col-start-5`}></div>
                <div className={`${trajectorySeverityCellClasses} border-r border-gray-300 col-start-6`}></div>
            </div>
            <div className="contents bg-gray-25">
                <div className={`${trajectoryIncluderCellClasses} border-l border-gray-300 col-start-1`}>
                    <label className={`flex gap-x-2 justify-start items-center ${mayIncludeMedicationReductionStatusInReport() ? "text-gray-600" : "text-gray-400"}`}>
                        <input
                            className="w-4 h-4 p-0.5 bg-gray-50 rounded border border-gray-300 cursor-pointer disabled:cursor-not-allowed"
                            type="checkbox"
                            checked={includeMedicationReduction}
                            disabled={(isGenerator && assessmentsGenerated) || !mayIncludeMedicationReductionStatusInReport()}
                            onChange={_e => setIncludeMedicationReduction(prev => !prev)}
                        />
                        Medication Reduction
                    </label>
                </div>
                <div className={`${trajectoryValueCellClasses} col-start-2`}>
                    {
                        mayIncludeMedicationReductionStatusInReport()
                            ? medicationReductionStatus.label
                            : renderNoTrajectoryValueDecorator()
                    }
                </div>
                <div className={`${trajectoryWeekCellClasses} col-start-3`}>
                    {
                        mayIncludeMedicationReductionStatusInReport() && reportWeek !== medicationReductionStatus.week &&
                        renderBandTrajectoryWeekDecorator(medicationReductionStatus.week)
                    }
                </div>
                <div className={`${trajectoryDeltaCellClasses} col-start-4`}></div>
                <div className={`${trajectorySeverityCellClasses} col-start-5`}></div>
                <div className={`${trajectorySeverityCellClasses} border-r border-gray-300 col-start-6`}></div>
            </div>
            <div className="col-span-6 h-3 bg-gray-25 border-b border-l border-r border-gray-300 rounded-b-md"></div>
            {/* --- End Program Impact Category --- */}
            {
                categories.map(category =>
                    renderTrajectoriesSection({
                        sectionTitle: category.title,
                        measureTypes: category.measureTypes
                    })
                )
            }
        </div>
    );
};